import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { errorToast, successToast } from '../../../Toast';
import {
	STATIC_DOCS_DELETE_FAILURE_MESSAGE,
	STATIC_DOCS_DELETE_SUCCESS_MESSAGE,
	STATIC_DOCS_UPLOAD_FAILURE_MESSAGE,
} from '../data/constants';
import loadAssetsClient from './loadAssetsClient';

const ssoApiClient = axios.create({
	baseURL: '/loadassets',
});

// GET request
interface StaticDocumentsRequest {
	asOfTimestamp: number;
	fileName: string;
}
// PUT response
export interface StaticDocumentResponse {
	locator: string;
	fileName: string;
	effectiveTimestamp: number;
	url: string;
	urlExpirationTimestamp: number;
}
// PUT request
export interface AddStaticDocumentRequest {
	asOfTimestamp: number;
	fileName: string;
	file: any;
}
// failed group static document response
interface ErrorDocumentResponse {
	fileName: string;
	effectiveTimestamp: number;
	errorDetails: string;
}
interface GroupStaticDocumentResponse {
	successfulDocuments: StaticDocumentResponse[];
	failedDocuments: ErrorDocumentResponse[];
}

const getStaticDocuments = async (
	request: StaticDocumentsRequest,
	authToken: string,
) => {
	let url = '/staticDocuments';

	const params = new URLSearchParams();

	if (request.asOfTimestamp) {
		params.append('asOfTimestamp', request.asOfTimestamp.toString());
	}
	if (request.fileName) {
		params.append('fileName', request.fileName);
	}

	if (Array.from(params).length > 0) {
		url += `?${params.toString()}`;
	}

	const res = await loadAssetsClient.get<StaticDocumentResponse[]>(url, {
		headers: {
			Authorization: authToken,
		},
	});

	return res.data;
};

const getStaticDocumentsSSO = async (
	request: StaticDocumentsRequest,
	tenantHostname: string,
) => {
	let url = '/staticDocuments';

	const params = new URLSearchParams();

	if (request.asOfTimestamp) {
		params.append('asOfTimestamp', request.asOfTimestamp.toString());
	}
	if (request.fileName) {
		params.append('fileName', request.fileName);
	}

	params.append('ns_tenant_host', tenantHostname);

	if (Array.from(params).length > 0) {
		url += `?${params.toString()}`;
	}

	const res = await ssoApiClient.get<StaticDocumentResponse[]>(url);

	return res.data;
};

const addStaticDocument = async (
	request: AddStaticDocumentRequest,
	authToken: string,
) => {
	const formData = new FormData();
	formData.append('file', request.file);
	request.asOfTimestamp &&
		formData.append('effectiveTimestamp', request.asOfTimestamp.toString());
	request.fileName && formData.append('fileName', request.fileName);

	let url = '/staticDocuments';

	const response = await loadAssetsClient.put<GroupStaticDocumentResponse>(
		url,
		formData,
		{
			headers: {
				Authorization: authToken,
			},
		},
	);
	return response.data;
};

const addStaticDocumentSSO = async (
	request: AddStaticDocumentRequest,
	tenantHostname: string,
) => {
	const formData = new FormData();
	formData.append('file', request.file);
	request.asOfTimestamp &&
		formData.append('effectiveTimestamp', request.asOfTimestamp.toString());
	request.fileName && formData.append('fileName', request.fileName);

	let url = '/staticDocuments?ns_tenant_host=' + tenantHostname;

	const response = await ssoApiClient.put<GroupStaticDocumentResponse>(
		url,
		formData,
	);
	return response.data;
};

const deleteStaticDocument = async (locator: string, authToken: string) => {
	let url = `/staticDocuments/${locator}`;
	const response = await loadAssetsClient.delete<any>(url, {
		headers: {
			Authorization: authToken,
		},
	});
	return response.data;
};

const deleteStaticDocumentSSO = async (
	locator: string,
	tenantHostname: string,
) => {
	let url = `/staticDocuments/${locator}?ns_tenant_host=${tenantHostname}`;
	const response = await ssoApiClient.delete<any>(url);
	return response.data;
};

// Queries
const useStaticDocuments = (
	queryKey: string[],
	request: StaticDocumentsRequest,
	token: string,
	tenantHostname?: string,
) => {
	return useQuery({
		queryKey,
		queryFn: async () => {
			return tenantHostname
				? await getStaticDocumentsSSO(request, tenantHostname)
				: await getStaticDocuments(request, token);
		},
	});
};

// Mutations
const useAddStaticDocumentMutation = (
	queryKey: string[],
	token: string,
	tenantHostname?: string,
) => {
	const client = useQueryClient();
	return useMutation(
		(newStaticDoc: AddStaticDocumentRequest) => {
			return tenantHostname
				? addStaticDocumentSSO(newStaticDoc, tenantHostname)
				: addStaticDocument(newStaticDoc, token);
		},
		{
			onError: (err) => {
				if (axios.isAxiosError(err) && err.response.data.error) {
					errorToast(
						STATIC_DOCS_UPLOAD_FAILURE_MESSAGE(err.response.data.error),
					);
				} else {
					errorToast(STATIC_DOCS_UPLOAD_FAILURE_MESSAGE());
				}
			},
			onSettled: () => {
				client.invalidateQueries(queryKey);
			},
		},
	);
};

const useDeleteStaticDocumentMutation = (
	queryKey: string[],
	token: string,
	tenantHostname?: string,
) => {
	const client = useQueryClient();
	return useMutation(
		(locator: string) => {
			return tenantHostname
				? deleteStaticDocumentSSO(locator, tenantHostname)
				: deleteStaticDocument(locator, token);
		},
		{
			onSuccess: () => {
				successToast(STATIC_DOCS_DELETE_SUCCESS_MESSAGE());
			},
			onError: (err) => {
				if (axios.isAxiosError(err) && err.response.data.message) {
					errorToast(
						STATIC_DOCS_DELETE_FAILURE_MESSAGE(err.response.data.message),
					);
				} else {
					errorToast(STATIC_DOCS_DELETE_FAILURE_MESSAGE());
				}
			},
			onSettled: () => {
				client.invalidateQueries(queryKey);
			},
		},
	);
};

const StaticDocumentsAPI = {
	getStaticDocuments,
	addStaticDocument,
	useStaticDocuments,
	useAddStaticDocumentMutation,
	useDeleteStaticDocumentMutation,
};

export default StaticDocumentsAPI;
